import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { getUserRoles, updateUserRoles } from "../../Services/AuthService";
import { Checkbox } from "@mui/material";
import Loader from "../Core/Loader";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "24px",
    boxShadow:
      "0 32px 64px rgba(5, 50, 81, 0.2), 0 16px 32px rgba(5, 50, 81, 0.1)",
    background: "linear-gradient(145deg, #ffffff 0%, #f8fffe 100%)",
    border: "1px solid rgba(124, 200, 144, 0.1)",
    position: "relative",
    overflow: "visible",
    maxWidth: "600px",
    width: "100%",
    margin: "16px",
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(5, 50, 81, 0.4)",
    backdropFilter: "blur(8px)",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background:
    "linear-gradient(135deg, rgba(5, 50, 81, 0.02) 0%, rgba(124, 200, 144, 0.02) 100%)",
  borderBottom: "1px solid rgba(5, 50, 81, 0.08)",
  padding: "32px 70px 24px 32px",
  position: "relative",
  direction: "rtl",
  textAlign: "right",
  "& span": {
    fontSize: "24px",
    fontWeight: "700",
    background: "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    lineHeight: "1.3",
    display: "block",
  },
}));

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  left: "16px",
  top: "16px",
  backgroundColor: "rgba(5, 50, 81, 0.05)",
  color: "#053251",
  width: "40px",
  height: "40px",
  transition: "all 0.3s",
  "&:hover": {
    backgroundColor: "#053251",
    color: "white",
    transform: "rotate(90deg)",
    boxShadow: "0 8px 16px rgba(5, 50, 81, 0.2)",
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "32px",
  paddingTop: "24px",
  paddingBottom: "24px",
  direction: "rtl",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
}));

const StyledButton = styled(Button)(({ theme, color }) => ({
  borderRadius: "14px",
  padding: "12px 28px",
  fontSize: "15px",
  fontWeight: "600",
  textTransform: "none",
  minWidth: "120px",
  transition: "all 0.3s",
  ...(color === "success" && {
    background: "linear-gradient(135deg, #7cc890 0%, #6bb57f 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #6bb57f 0%, #5aa46e 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 16px rgba(124, 200, 144, 0.3)",
    },
  }),
  ...(color === "error" && {
    background: "linear-gradient(135deg, #ff6b6b 0%, #e55a5a 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #e55a5a 0%, #d14949 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 16px rgba(255, 107, 107, 0.3)",
    },
  }),
  ...(color === "primary" && {
    background: "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
    color: "white",
    border: "1px solid rgba(5, 50, 81, 0.3)",
    "&:hover": {
      background: "linear-gradient(135deg, #2a5a72 0%, #1e4159 100%)",
      boxShadow: "0 12px 24px rgba(5, 50, 81, 0.3)",
      transform: "translateY(-2px)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  }),
}));

const EditModal = ({ open, handleClose, user, handleSave }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        const data = await getUserRoles(user.id);
        setRoles(data);
      } catch (error) {
        console.error("Error fetching roles:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchRoles();
    }
  }, [user]);

  const onSave = async () => {
    setLoading(true);

    try {
      await updateUserRoles(user.id, roles);
      handleSave(roles);
    } catch (error) {
      console.error("Error updating roles:", error);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      keepMounted
    >
      <StyledDialogTitle>
        <span>تغییر دسترسی</span>
        <StyledCloseButton onClick={handleClose}>
          <CloseIcon />
        </StyledCloseButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {loading ? (
          <Loader />
        ) : (
          <>
            {" "}
            <div className="container mt-4">
              {roles.map((role) => (
                <>
                  <div className="me-3 d-flex align-items-center mb-1">
                    {" "}
                    <div key={role.roleId}>
                      <Checkbox
                        checked={role.isAssigned || false}
                        onChange={(e) => {
                          const updatedRoles = roles.map((r) =>
                            r.roleId === role.roleId
                              ? { ...r, isAssigned: e.target.checked }
                              : r
                          );
                          setRoles(updatedRoles);
                        }}
                        name={role.roleName}
                        color="primary"
                        size="medium"
                      />
                    </div>
                    <div className="fs-6">
                      <span>{role.roleNameFa}</span>
                    </div>
                  </div>
                </>
              ))}
            </div>
          </>
        )}
        <div className="d-flex justify-content-start gap-3 mt-4">
          <StyledButton color="primary" onClick={handleClose}>
            <span>انصراف</span>
          </StyledButton>
          <StyledButton color="success" onClick={onSave}>
            <span>ذخیره</span>
          </StyledButton>
        </div>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default EditModal;
